import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, MessageSquare, CheckSquare, UserCog,
  BookOpen, Settings, BarChart3, LogOut, Menu, X, Bell,
} from "lucide-react";
import { crmApi } from "../../lib/crmApi";

const NAV = [
  { to: "/admin/crm", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/crm/leads", label: "Leads", icon: Users },
  { to: "/admin/crm/conversations", label: "Conversations", icon: MessageSquare },
  { to: "/admin/crm/sales-ops", label: "Sales Ops", icon: BarChart3 },
  { to: "/admin/crm/insights", label: "Insights", icon: BarChart3 },
  { to: "/admin/crm/tasks", label: "Follow-ups", icon: CheckSquare },
  { to: "/admin/crm/staff", label: "Sales Staff", icon: UserCog, adminOnly: true },
  { to: "/admin/crm/knowledge", label: "AI Knowledge", icon: BookOpen, adminOnly: true },
  { to: "/admin/crm/library", label: "Library", icon: BookOpen, adminOnly: true },
  { to: "/admin/crm/settings", label: "AI Settings", icon: Settings, adminOnly: true },
  { to: "/admin/crm/reports", label: "Reports", icon: BarChart3 },
];

export default function CrmLayout() {
  const navigate = useNavigate();
  const [actor, setActor] = useState(null);
  const [err, setErr] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  const loadNotifs = useCallback(() => {
    crmApi.notifications().then((d) => { setNotifs(d.notifications); setUnread(d.unread); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!actor) return;
    loadNotifs();
    const t = setInterval(loadNotifs, 60000);
    return () => clearInterval(t);
  }, [actor, loadNotifs]);

  const openNotif = async (n) => {
    setNotifOpen(false);
    if (!n.read) crmApi.markNotificationRead(n.id).then(loadNotifs).catch(() => {});
    if (n.lead_id) navigate(`/admin/crm/leads/${n.lead_id}`);
  };

  useEffect(() => {
    crmApi.me()
      .then(setActor)
      .catch(() => navigate("/crm/login", { replace: true }));
  }, [navigate]);

  const logout = async () => {
    try { await crmApi.logout(); } catch { /* cookie may already be gone */ }
    localStorage.removeItem("tf_crm_token");
    localStorage.removeItem("tf_admin_token");
    navigate("/crm/login", { replace: true });
  };

  if (!actor) {
    return (
      <div className="min-h-screen bg-[#FAF5EC] flex items-center justify-center">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F]">{err || "Loading CRM…"}</p>
      </div>
    );
  }

  const items = NAV.filter((n) => !n.adminOnly || actor.type === "admin");

  return (
    <div className="min-h-screen bg-[#FAF5EC] flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-60 shrink-0 bg-[#1C110C] min-h-screen sticky top-0 max-h-screen">
        <div className="px-6 py-6 border-b border-white/10">
          <p className="font-serif-display text-lg tracking-[0.18em] text-[#EDE1C8] uppercase">Time · Furniture</p>
          <p className="text-[9px] tracking-[0.3em] uppercase text-[#A6822F] mt-1">AI Sales CRM</p>
        </div>
        <nav className="flex-1 py-4">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`crm-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-[10px] tracking-[0.22em] uppercase transition-colors ${
                  isActive ? "text-[#EDE1C8] bg-[#6E1F2C]" : "text-[#EDE1C8]/50 hover:text-[#EDE1C8] hover:bg-white/5"
                }`
              }
            >
              <n.icon size={14} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-5 border-t border-white/10">
          <p className="text-[10px] text-[#EDE1C8]/60 mb-2 truncate" data-testid="crm-actor-name">
            {actor.name} · {actor.type === "admin" ? "Admin" : "Sales"}
          </p>
          <button
            type="button"
            onClick={logout}
            data-testid="crm-logout-btn"
            className="inline-flex items-center gap-2 text-[9px] tracking-[0.24em] uppercase text-[#A6822F] hover:text-[#EDE1C8]"
          >
            <LogOut size={12} /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 bg-[#1C110C] px-4 py-3 flex items-center justify-between">
          <div>
            <p className="font-serif-display text-sm tracking-[0.16em] text-[#EDE1C8] uppercase">TF · AI Sales CRM</p>
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="crm-mobile-menu-toggle"
            className="text-[#EDE1C8]"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </header>
        {menuOpen && (
          <nav className="lg:hidden bg-[#1C110C] border-t border-white/10 pb-3">
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-[10px] tracking-[0.22em] uppercase ${
                    isActive ? "text-[#EDE1C8] bg-[#6E1F2C]" : "text-[#EDE1C8]/60"
                  }`
                }
              >
                <n.icon size={14} /> {n.label}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={logout}
              className="flex items-center gap-3 px-6 py-3 text-[10px] tracking-[0.22em] uppercase text-[#A6822F]"
            >
              <LogOut size={14} /> Log out
            </button>
          </nav>
        )}

        <main className="p-4 md:p-8 max-w-[1300px]">
          {/* Notifications bell */}
          <div className="flex justify-end mb-2 relative">
            <button
              type="button"
              onClick={() => setNotifOpen((v) => !v)}
              data-testid="crm-notif-bell"
              className="relative text-[#6E1F2C] hover:text-[#56151F] p-2"
              aria-label="Notifications"
            >
              <Bell size={18} />
              {unread > 0 && (
                <span data-testid="crm-notif-count" className="absolute -top-0.5 -right-0.5 bg-[#A3141A] text-white text-[9px] min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-10 z-50 w-[340px] max-h-[420px] overflow-y-auto bg-white border border-[#6E1F2C]/20 shadow-xl" data-testid="crm-notif-panel">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#6E1F2C]/10 sticky top-0 bg-white">
                  <p className="text-[9px] tracking-[0.24em] uppercase text-[#6E1F2C]">Notifications</p>
                  {unread > 0 && (
                    <button type="button" onClick={() => crmApi.markAllNotificationsRead().then(loadNotifs)} data-testid="crm-notif-readall" className="text-[9px] tracking-[0.14em] uppercase text-[#A6822F] hover:text-[#6E1F2C]">
                      Mark all read
                    </button>
                  )}
                </div>
                {notifs.length === 0 && <p className="text-xs text-[#7A6B5F] italic px-4 py-4">No notifications yet.</p>}
                {notifs.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => openNotif(n)}
                    className={`block w-full text-left px-4 py-3 border-b border-[#6E1F2C]/8 hover:bg-[#FAF5EC] ${n.read ? "opacity-60" : ""}`}
                  >
                    <p className="text-[12px] text-[#1C110C] font-medium">{n.title}</p>
                    {n.body && <p className="text-[11px] text-[#7A6B5F] mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[10px] text-[#A6822F] mt-0.5">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Outlet context={{ actor }} />
        </main>
      </div>
    </div>
  );
}
